import type { Row } from 'write-excel-file/browser';
import { cell, columnWidths, headerCell } from '@/lib/xlsxSheet';
import type { SheetContent } from '@/lib/xlsxSheet';
import { toImageLabel } from '../types/LabelingTypes';
import type { ImageLabel, LabelingSession, LabelingSessionImage } from '../types/LabelingTypes';

/** The part of a labeling session the export needs: which tool, and whose labels these are. */
export type ExportSession = Pick<
  LabelingSession,
  'sessionId' | 'experimentId' | 'name' | 'createdBy'
>;

/** One annotator's session and every image of its experiment, with that annotator's labels. */
export interface SessionLabelData {
  session: ExportSession;
  images: LabelingSessionImage[];
}

/**
 * One tool and the sessions that hold labels for it — one per annotator, none when nobody labeled
 * it yet. An experiment follows a single tool across the parts it machines, so the tool is
 * identified by its experiment id.
 */
export interface ToolLabelData {
  experimentId: string;
  sessions: SessionLabelData[];
}

/** Where in the experiment an image sits: the machined part, the operation and the capture time. */
export interface ImagePoint {
  partId: string | null;
  operationId: string | null;
  /** Backend timestamp, passed through verbatim (ISO 8601 with zone) — never re-formatted. */
  capturedAt: string | null;
}

/** Wear-out of one tool as one annotator labeled it. */
export interface ToolWearSummary {
  tool: string;
  machine: string | null;
  /** Last OK image before `firstBadState` (or the last OK image overall while nothing is worn). */
  lastGoodState: ImagePoint | null;
  /** Chronologically first image labeled worn: the tool broke between the two states. */
  firstBadState: ImagePoint | null;
  hasUnreadableImages: boolean;
  imageCount: number;
  labeledCount: number;
  okCount: number;
  wornCount: number;
  unreadableCount: number;
  /** OK labels captured after `firstBadState`: a non-zero count flags inconsistent labeling. */
  okAfterWornCount: number;
  session: string;
  labeledBy: string;
}

/**
 * Sheet and column names are fixed, English and snake_case on purpose: the workbook is a dataset
 * meant to be read by scripts, so its schema must not change with the UI language.
 */
export const TOOLS_SHEET = 'tools';
export const IMAGES_SHEET = 'images';

export const TOOL_COLUMNS = [
  'tool',
  'machine',
  'last_good_state',
  'last_good_part',
  'last_good_operation',
  'first_bad_state',
  'first_bad_part',
  'first_bad_operation',
  'has_unreadable_images',
  'image_count',
  'labeled_count',
  'ok_count',
  'worn_count',
  'unreadable_count',
  'ok_after_worn_count',
  'session',
  'labeled_by',
] as const;

export const IMAGE_COLUMNS = [
  'tool',
  'part',
  'operation',
  'stack',
  'captured_at',
  'machine',
  'label',
  'session',
  'labeled_by',
  'image_key',
] as const;

export interface LabelExportSheets {
  /** One row per labeled tool and annotator: the last good and first bad states they recorded. */
  tools: SheetContent;
  /** One row per labeled image, chronological within each tool. */
  images: SheetContent;
}

type LabeledImage = LabelingSessionImage & { label: ImageLabel };

function isLabeled(image: LabelingSessionImage): image is LabeledImage {
  return toImageLabel(image.label) !== null;
}

function hasLabels({ images }: SessionLabelData): boolean {
  return images.some(isLabeled);
}

/**
 * Ascending by capture time, stable tie-break on `imageKey`; images without a capture time go
 * last. The backend lists images by `imageKey`, which puts operation "10" before operation "2".
 */
export function sortChronologically(images: LabelingSessionImage[]): LabelingSessionImage[] {
  const time = (image: LabelingSessionImage) =>
    image.createdAt === null ? Number.NaN : Date.parse(image.createdAt);
  return [...images].sort((a, b) => {
    const timeA = time(a);
    const timeB = time(b);
    if (Number.isNaN(timeA) !== Number.isNaN(timeB)) return Number.isNaN(timeA) ? 1 : -1;
    if (!Number.isNaN(timeA) && timeA !== timeB) return timeA - timeB;
    return a.imageKey < b.imageKey ? -1 : a.imageKey > b.imageKey ? 1 : 0;
  });
}

function toPoint(image: LabelingSessionImage): ImagePoint {
  return { partId: image.partId, operationId: image.operationId, capturedAt: image.createdAt };
}

function count(images: LabeledImage[], label: ImageLabel): number {
  return images.filter((image) => image.label === label).length;
}

function summariseSession({ session, images }: SessionLabelData): ToolWearSummary {
  const labeled = sortChronologically(images).filter(isLabeled);
  // Unreadable images say nothing about wear: the two states are read off the verdicts only.
  const verdicts = labeled.filter((image) => image.label !== 'unreadable');
  const firstWornIndex = verdicts.findIndex((image) => image.label === 'worn');
  const hasWorn = firstWornIndex !== -1;
  // Every verdict before the first worn image is OK by construction.
  const lastOkIndex = (hasWorn ? firstWornIndex : verdicts.length) - 1;
  const unreadableCount = count(labeled, 'unreadable');

  return {
    tool: session.experimentId,
    machine: images.find((image) => image.machineName)?.machineName ?? null,
    lastGoodState: lastOkIndex >= 0 ? toPoint(verdicts[lastOkIndex]) : null,
    firstBadState: hasWorn ? toPoint(verdicts[firstWornIndex]) : null,
    hasUnreadableImages: unreadableCount > 0,
    imageCount: images.length,
    labeledCount: labeled.length,
    okCount: count(labeled, 'ok'),
    wornCount: count(labeled, 'worn'),
    unreadableCount,
    okAfterWornCount: hasWorn ? count(verdicts.slice(firstWornIndex), 'ok') : 0,
    session: session.name,
    labeledBy: session.createdBy,
  };
}

/** Several people may label the same tool: annotators first, then their sessions by name. */
function compareSessions(a: SessionLabelData, b: SessionLabelData): number {
  // Sessions predating per-user ownership carry no `createdBy` at all.
  return (
    (a.session.createdBy ?? '').localeCompare(b.session.createdBy ?? '') ||
    a.session.name.localeCompare(b.session.name)
  );
}

function labeledSessions({ sessions }: ToolLabelData): SessionLabelData[] {
  return sessions.filter(hasLabels).sort(compareSessions);
}

/** One summary per annotator session holding labels — none for a tool nobody labeled. */
export function summariseTool(tool: ToolLabelData): ToolWearSummary[] {
  return labeledSessions(tool).map(summariseSession);
}

/** True when at least one tool holds a label, i.e. the export would not be empty. */
export function hasLabeledData(data: ToolLabelData[]): boolean {
  return data.some((tool) => tool.sessions.some(hasLabels));
}

function compareTools(a: ToolLabelData, b: ToolLabelData): number {
  return a.experimentId.localeCompare(b.experimentId, undefined, { numeric: true });
}

export function buildLabelExportSheets(data: ToolLabelData[]): LabelExportSheets {
  const tools = [...data].sort(compareTools);

  const toolRows: Row[] = [
    TOOL_COLUMNS.map(headerCell),
    ...tools
      .flatMap(summariseTool)
      .map((summary) =>
        [
          summary.tool,
          summary.machine,
          summary.lastGoodState?.capturedAt,
          summary.lastGoodState?.partId,
          summary.lastGoodState?.operationId,
          summary.firstBadState?.capturedAt,
          summary.firstBadState?.partId,
          summary.firstBadState?.operationId,
          summary.hasUnreadableImages,
          summary.imageCount,
          summary.labeledCount,
          summary.okCount,
          summary.wornCount,
          summary.unreadableCount,
          summary.okAfterWornCount,
          summary.session,
          summary.labeledBy,
        ].map(cell)
      ),
  ];

  const imageRows: Row[] = [
    IMAGE_COLUMNS.map(headerCell),
    ...tools.flatMap((tool) =>
      labeledSessions(tool).flatMap(({ session, images }) =>
        sortChronologically(images)
          .filter(isLabeled)
          .map((image) =>
            [
              session.experimentId,
              image.partId,
              image.operationId,
              image.stackId,
              image.createdAt,
              image.machineName,
              image.label,
              session.name,
              session.createdBy,
              image.imageKey,
            ].map(cell)
          )
      )
    ),
  ];

  return {
    tools: { rows: toolRows, columns: columnWidths(toolRows) },
    images: { rows: imageRows, columns: columnWidths(imageRows) },
  };
}
